import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { Checkbox } from "../ui/checkbox";
import { api, errorMessage } from "../../lib/api";

const LABELS = { name: "Name", email: "Email", phone: "Phone", role: "Role", organization: "Organization", service: "Service", services: "Services", delivery: "Format", location: "Location", participants: "People", timeframe: "Timeframe", message: "Message", details: "Details", event_type: "Event type", event_name: "Event", topics: "Topics", format: "Format", dates: "Dates", audience: "Audience", resource: "Resource", follow_up: "Follow-up" };
const HIDDEN = ["resource_id"];
const FILTERS = [["needs", "Needs follow-up"], ["all", "All"], ["done", "Contacted"], ["download", "Downloads"]];

const RequestItem = ({ r, onToggle }) => (
  <li className={`bg-paper rounded-2xl p-5 ${r.contacted ? "opacity-75" : ""}`} data-testid={`admin-request-${r.id}`}>
    <div className="flex flex-wrap items-center gap-3">
      <span className={`lbl rounded-full px-3 py-1 ${r.kind === "quote" ? "bg-sand" : "bg-sky"}`}>{r.kind}</span>
      {r.needs_follow_up && <span className="lbl rounded-full px-3 py-1 bg-teal text-paper">Needs follow-up</span>}
      <span className="text-sm text-slate">{new Date(r.created_at).toLocaleString()}</span>
      {!r.email_sent && r.kind !== "download" && <span className="text-sm text-destructive">Email not delivered</span>}
      <span className="ml-auto flex items-center gap-2">
        <Checkbox id={`contacted-${r.id}`} checked={r.contacted} onCheckedChange={(v) => onToggle(r, !!v)} className="border-ink data-[state=checked]:bg-teal data-[state=checked]:border-teal" data-testid={`request-contacted-${r.id}`} />
        <label htmlFor={`contacted-${r.id}`} className="text-sm cursor-pointer">{r.contacted ? `Contacted ${r.contacted_at ? new Date(r.contacted_at).toLocaleDateString() : ""}` : "Mark as contacted"}</label>
      </span>
    </div>
    <dl className="mt-3 grid sm:grid-cols-2 gap-x-8 gap-y-2 text-[15px]">
      {Object.entries(r.data).filter(([k, v]) => !HIDDEN.includes(k) && v && v.length !== 0).map(([k, v]) => (
        <div key={k} className={["message", "details"].includes(k) ? "sm:col-span-2" : ""}>
          <dt className="lbl text-slate">{LABELS[k] || k}</dt>
          <dd className="whitespace-pre-wrap break-words">{k === "email" ? <a className="text-teal underline" href={`mailto:${v}`}>{v}</a> : Array.isArray(v) ? v.join(", ") : v}</dd>
        </div>
      ))}
    </dl>
  </li>
);

export const RequestsPanel = () => {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("needs");
  useEffect(() => { api.get("/admin/requests").then((r) => setItems(r.data)).catch((e) => toast.error(errorMessage(e))); }, []);

  const counts = useMemo(() => ({
    needs: (items || []).filter((r) => r.needs_follow_up).length, all: (items || []).length,
    done: (items || []).filter((r) => r.contacted).length, download: (items || []).filter((r) => r.kind === "download").length,
  }), [items]);
  const shown = (items || []).filter((r) => filter === "all" || (filter === "needs" && r.needs_follow_up) || (filter === "done" && r.contacted) || (filter === "download" && r.kind === "download"));

  const toggle = async (r, contacted) => {
    try {
      await api.put(`/admin/requests/${r.id}/contacted`, { contacted });
      setItems((xs) => xs.map((x) => x.id === r.id ? { ...x, contacted, contacted_at: contacted ? new Date().toISOString() : "", needs_follow_up: contacted ? false : (x.kind !== "download" || String(x.data.follow_up || "").startsWith("Yes")) } : x));
      toast.success(contacted ? "Marked as contacted" : "Moved back to needs follow-up");
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/requests/export", { params: { kind: "download" }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `resource-contacts-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed. Please try again."); }
  };

  if (!items) return <p className="font-sans text-slate">Loading...</p>;
  return (
    <div className="font-sans" data-testid="admin-requests">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div role="tablist" aria-label="Filter requests" className="flex flex-wrap gap-2">
          {FILTERS.map(([k, l]) => (
            <button key={k} role="tab" aria-selected={filter === k} onClick={() => setFilter(k)} className={`rounded-full px-4 py-2 text-sm border transition-colors ${filter === k ? "bg-teal text-paper border-teal" : "border-ink/30 hover:bg-sky"}`} data-testid={`requests-filter-${k}`}>
              {l} <span className="opacity-80">({counts[k]})</span>
            </button>
          ))}
        </div>
        <button onClick={exportCsv} className="inline-flex items-center gap-2 border-2 border-teal text-teal rounded-full px-5 py-2 hover:bg-teal hover:text-paper transition-colors" data-testid="requests-export-button">
          <Download className="w-4 h-4" aria-hidden="true" /> Export resource contacts (CSV)
        </button>
      </div>
      {shown.length === 0 && <p className="text-slate" data-testid="admin-requests-empty">{filter === "needs" ? "You're all caught up. Nobody is waiting on a reply." : "Nothing here yet."}</p>}
      <ul className="space-y-4" data-testid="admin-requests-list">
        {shown.map((r) => <RequestItem key={r.id} r={r} onToggle={toggle} />)}
      </ul>
    </div>
  );
};
